import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import type {
  IHeader,
  IHeaderCellProps,
} from "../../components/Organismos/Table/FilteredTable.types";
import { getAllFirmNames } from "../../Services/Firm";
import { firmLegalName, firmLongName } from "../../utils/firmDisplayName";
import { formatBillingStatusLabel } from "../../utils/billingStatus";

const INVOICE_KIND_LABELS: Record<string, string> = {
  membershipDues: "Membership Dues",
  annualMeeting: "Annual Meeting",
  lateFee: "Late Fee",
  special: "Special",
  contingency: "Contingency",
  event: "Event",
  other: "Other",
};

function formatMoney(value: unknown): string {
  const num = Number(value ?? 0);
  if (Number.isNaN(num)) return "";
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function InvoicesList(): React.ReactElement {
  const endpoint = "/invoices";
  const [firmNameById, setFirmNameById] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const firms = await getAllFirmNames();
      if (cancelled) return;
      const map: Record<string, string> = {};
      for (const f of firms) {
        const id = (f as { id?: string }).id;
        if (id) {
          map[id] = firmLegalName(f) || firmLongName(f) || id;
        }
      }
      setFirmNameById(map);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const partyCell = useCallback(
    ({ row }: IHeaderCellProps) => {
      const label = row.party as string;
      if (row.partyType === "firm" && row.partyId) {
        return (
          <Link
            to={`/accounting/${row.partyId}`}
            className="text-[#0070CD] hover:underline"
          >
            {label}
          </Link>
        );
      }
      return <span>{label}</span>;
    },
    [],
  );

  const columnsDef = useMemo<IHeader[]>(
    () => [
      { id: "invoiceNumber", header: "Invoice #", width: 120 },
      { id: "party", header: "Firm / Party", width: 200, cell: partyCell },
      { id: "fiscalYear", header: "FY", width: 70, enableSorting: false },
      { id: "kind", header: "Type", width: 140, enableSorting: false },
      { id: "issueDate", header: "Issue Date", width: 110 },
      { id: "dueDate", header: "Due Date", width: 110 },
      { id: "total", header: "Total", width: 110 },
      { id: "totalPaid", header: "Paid", width: 110 },
      { id: "balance", header: "Balance", width: 110 },
      { id: "status", header: "Status", width: 120 },
    ],
    [partyCell],
  );

  const formatData = useCallback(
    (data: any[]) => {
      if (!Array.isArray(data)) return [];
      return data.map((d) => {
        const partyId: string = d.partyId ?? "";
        const resolvedName =
          d.partyType === "firm"
            ? firmNameById[partyId] ?? firmNameById[d.firmId] ?? partyId
            : partyId;
        return {
          ...d,
          party: resolvedName,
          fiscalYear: d.context?.fiscalYear ?? "",
          kind: INVOICE_KIND_LABELS[d.context?.kind] ?? d.context?.kind ?? "",
          total: formatMoney(d.total),
          totalPaid: formatMoney(d.totalPaid),
          balance: formatMoney(d.balance),
          status: formatBillingStatusLabel(d.status),
        };
      });
    },
    [firmNameById],
  );

  return (
    <div className="flex min-w-0 flex-col gap-4 p-6">
      <h1 className="text-xl font-bold text-primary">Invoices</h1>
      <FilteredTableCore
        tableKey="/accounting/invoices"
        columnsDef={columnsDef}
        endpoint={endpoint}
        showRowNumber
        showTopHorizontalScrollBar
        filtersData={[]}
        formatData={formatData}
        defaultSorting={{ field: "issueDate", order: "desc" }}
        seedDefaultSortingPreference
        defaultPageSize={50}
        emptyStateMessage="No invoices found."
        excelFilename="invoices"
      />
    </div>
  );
}
